import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LIGHT_ACCORDION } from '@yme/atoms/accordion';
import { AlertType } from '@yme/atoms/alert';
import { Col, Container, Row } from '@yme/atoms/grid';
import { ACCESSORIES_ATTRIBUTE, CONFLICTING_ITEMS_KEY, PRODUCTS_ATTRIBUTE, PRODUCT_DETAILS_KEY, ProductType, USPS_KEY, } from '@yme/common/constants';
import { buildAccordionData } from '@yme/components/FAQContent/helpers';
import { IndentWrapper } from '@yme/components/Separator';
import { setIsModalShowed } from '@yme/modals/actions';
import { MODALS } from '@yme/modals/constants';
import { CheckPartAlert } from '@yme/pdp-components/CheckPartAlert';
import { AccordionStyled, FaqContentWrapperStyled, FaqWrapperStyled, ProductDetailsItemStyled, ProductDetailsListStyled, } from '@yme/pdp-components/DescriptionFaq/styled';
import { setCompatibilityCheckAllowed, getCompatibilityCheckAllowed, getProductCardLabels, getSelectedCompatibilityProduct, } from '@yme/pdp-components/ProductCard';
import { getSuitableRequiredItems, openSelectModelModal, setSelectedUnit, } from '@yme/pdp-components/ProductCard/CardAccessories/helpers';
import { buildUrl } from '@yme/pdp-components/ProductFeatures/helpers';
import { getFeaturesIDs } from '@yme/pdp-components/ProductFeatures/redux/selectors';
import { getCommonLabels, getRTL } from '@yme/selectors/common';
import { ACCESSORY_PDP_HEADING_2 } from '@yme/selectors/constants';
import { getAttribute, getIsLoadedProductDetails, getProductDetails, } from '@yme/selectors/products';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel2 } from '@yme/tokens/typography';
import { checkAccessoryHideProductCompatibility, getAttributeByName } from '@yme/utils/common';
import { ProductParts } from '../ProductPart';
import { DescriptionFaqSkeleton } from './DescriptionFaqSkeleton';
import { COMPATIBILITY_ANCHOR } from './constants';
export function DescriptionFaq({ hyperdriveEndpoints: { productsByIds, usps }, labels, isRemoveBottomIndent, faqSection, selectModelModalModel, privacyConsentsModel, isInEditor, }) {
    const [faqData, setFaqData] = useState({});
    const dispatch = useDispatch();
    const isRightToLeft = useSelector(getRTL);
    const commonLabels = useSelector(getCommonLabels);
    const selectedCompatibilityProduct = useSelector(getSelectedCompatibilityProduct);
    const productCardLabels = useSelector(getProductCardLabels);
    const isCompatibilityCheckAllowed = useSelector(getCompatibilityCheckAllowed);
    const isLoadedProductDetails = useSelector(getIsLoadedProductDetails);
    const accessoryPdpHeadingLevel2 = useSelector(getAttribute(ACCESSORY_PDP_HEADING_2));
    const featuresIDs = useSelector(getFeaturesIDs(USPS_KEY));
    const conflictingItemsIDs = useSelector(getFeaturesIDs(CONFLICTING_ITEMS_KEY));
    const { data: productDetailsData } = useSelector(getProductDetails);
    const isAccessoryHideProductCompatibility = checkAccessoryHideProductCompatibility(productDetailsData);
    const isAccessory = productDetailsData?.type === ProductType.ACCESSORY;
    const isApparel = productDetailsData?.type === ProductType.APPAREL;
    const isAccessoryOrApparel = isAccessory || isApparel;
    const { partsAccessoriesMerch_productDetails_title: productDetailsTitle, partsAccessoriesMerch_compatibilityAndConflictingParts_title: compatibilityAndConflictingPartsTitle, partsAccessoriesMerch_compatibleWithYourModelAlert_description: compatibleWithYourModelAlertDescription, partsAccessoriesMerch_viewDetails_label: viewDetailsLabel, partsAccessoriesMerch_conflictingItems_title: conflictingItemsTitle, partsAccessoriesMerch_conflictingItems_description: conflictingItemsDescription, partsAccessoriesMerch_selectYourModelAlert_description: selectYourModelAlert, partsAccessoriesMerch_selectModel_label: selectYourModelLink, partsAccessoriesMerch_changeModel_label: changeModelLink, } = labels;
    const { partsAccessoriesMerch_resetModel_link: resetModelLink, partsAccessoriesMerch_addToCartPopUpYouSure_header: youSureHeader, partsAccessoriesMerch_resetModelPopUp_description: resetModelDescription, } = productCardLabels;
    const { labels: modalLabels, defaultStartYear, categories, endpoints } = selectModelModalModel;
    const resetModelHandler = () => {
        dispatch(setIsModalShowed({
            type: MODALS.RESET_MODEL_MODAL,
            data: {
                title: youSureHeader,
                description: resetModelDescription,
            },
        }));
    };
    const compatibilityAlertData = selectedCompatibilityProduct?.isSuccessCheck
        ? {
            type: AlertType.SUCCESS,
            message: `${compatibleWithYourModelAlertDescription} ${selectedCompatibilityProduct.product.name}`,
            buttonText: changeModelLink,
            secondaryButtonText: resetModelLink,
            secondaryClickHandler: resetModelHandler,
        }
        : {
            type: AlertType.MESSAGE,
            message: selectYourModelAlert,
            buttonText: selectYourModelLink,
        };
    useEffect(() => {
        const isCheckAllowed = selectedCompatibilityProduct !== null &&
            (!selectedCompatibilityProduct?.isSuccessCheck || !faqData?.conflictingItems?.length);
        dispatch(setCompatibilityCheckAllowed(!isCheckAllowed && isAccessory));
    }, [dispatch, faqData?.conflictingItems?.length, isAccessory, selectedCompatibilityProduct]);
    const suitableProductsKeys = useSelector(getFeaturesIDs(PRODUCTS_ATTRIBUTE));
    const checkCompatibility = (product) => {
        if (product) {
            setSelectedUnit(product.key, suitableProductsKeys, product, dispatch, true);
        }
    };
    const openModalHandler = () => {
        openSelectModelModal({
            dispatch,
            labels: modalLabels,
            commonLabels,
            endpoints,
            defaultStartYear,
            categories,
            callback: checkCompatibility,
        });
    };
    const prepareAccordionData = (data) => {
        const productDetailsListData = (React.createElement(FaqContentWrapperStyled, { isRightToLeft: isRightToLeft },
            React.createElement(ProductDetailsListStyled, null, data?.productDetails?.map((item) => (React.createElement(ProductDetailsItemStyled, { key: item.id }, item.value.description))))));
        const conflictingItems = (React.createElement(FaqContentWrapperStyled, { isRightToLeft: isRightToLeft },
            React.createElement(CheckPartAlert, { primaryClickHandler: openModalHandler, isRightToLeft: isRightToLeft, ...compatibilityAlertData }),
            React.createElement(ProductParts, { data: data?.conflictingItems, title: conflictingItemsTitle, description: conflictingItemsDescription, viewDetailsLabel: viewDetailsLabel, privacyConsentsModel: privacyConsentsModel })));
        return [
            data.productDetails
                ? {
                    title: productDetailsTitle,
                    children: productDetailsListData,
                    isRightToLeft,
                }
                : null,
            isCompatibilityCheckAllowed && !isAccessoryHideProductCompatibility
                ? {
                    title: compatibilityAndConflictingPartsTitle,
                    children: conflictingItems,
                    id: COMPATIBILITY_ANCHOR,
                    isRightToLeft,
                }
                : null,
        ];
    };
    const getData = (url, key) => fetch(url)
        .then((response) => response.json())
        .then((responseData) => {
        setFaqData((prev) => ({
            ...prev,
            [key]: responseData,
        }));
    })
        .catch(() => {
        setFaqData((prev) => ({
            ...prev,
            [key]: key === CONFLICTING_ITEMS_KEY ? [] : prev[key],
        }));
    });
    useEffect(() => {
        if (featuresIDs && featuresIDs.length > 0) {
            getData(buildUrl(usps, featuresIDs), PRODUCT_DETAILS_KEY);
        }
        if (conflictingItemsIDs && conflictingItemsIDs.length > 0) {
            const selectedCompatibilityProductAccessories = getAttributeByName(selectedCompatibilityProduct?.product.variants[0].attributes ?? [], ACCESSORIES_ATTRIBUTE);
            const suitableRequiredItemKeys = getSuitableRequiredItems(conflictingItemsIDs, selectedCompatibilityProductAccessories);
            const keys = suitableRequiredItemKeys.length ? suitableRequiredItemKeys : '';
            getData(buildUrl(`${productsByIds}\${keys}`, keys), CONFLICTING_ITEMS_KEY);
        }
    }, [
        conflictingItemsIDs,
        featuresIDs,
        productsByIds,
        selectedCompatibilityProduct?.product,
        usps,
    ]);
    const accordionData = [
        ...prepareAccordionData(faqData),
        ...buildAccordionData(faqSection?.items, isRightToLeft),
    ];
    if (!isInEditor && !isLoadedProductDetails) {
        return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent },
            React.createElement(DescriptionFaqSkeleton, null)));
    }
    if (!accordionData.length) {
        return null;
    }
    const preOpenedIndex = isApparel ? 0 : isCompatibilityCheckAllowed ? 1 : 0;
    return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent },
        React.createElement(Container, null,
            React.createElement(FaqWrapperStyled, null,
                accessoryPdpHeadingLevel2 && isAccessoryOrApparel && (React.createElement(Row, null,
                    React.createElement(Col, null,
                        React.createElement(FlexWrapper, { isFullWidth: true },
                            React.createElement(HeadingLevel2, null, accessoryPdpHeadingLevel2))))),
                React.createElement(Row, null,
                    React.createElement(Col, { lg: 12, md: 8 },
                        React.createElement(AccordionStyled, { className: "description-faq-accordion", isRightToLeft: isRightToLeft, isContentStylesCustom: true, data: accordionData, preOpenedIndex: preOpenedIndex, colorScheme: LIGHT_ACCORDION })))))));
}
DescriptionFaq.displayName = 'DescriptionFaq_AEMComponent';
